import React, { useState, useEffect } from 'react';
import { db, storage } from '../firebaseConfig';
import {
  collection,
  addDoc,
  getDocs,
  query,
  where,
  doc,
  deleteDoc,
  updateDoc,
  orderBy,
  serverTimestamp,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './ManageOffers.css';

const ManageRetailOffers = () => {
  const [offers, setOffers] = useState([]);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [price, setPrice] = useState('');
  const [discountType, setDiscountType] = useState('percentage');
  const [discountValue, setDiscountValue] = useState('');
  const [minRequirement, setMinRequirement] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [active, setActive] = useState(true);
  const [message, setMessage] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [editOfferId, setEditOfferId] = useState(null);

  const coffeeShopId = localStorage.getItem('coffee_shop_id');
  const coffeeShopName = localStorage.getItem('coffee_shop_name');
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    fetchOffers();
  }, []);

  const fetchOffers = async () => {
    const snap = await getDocs(query(
      collection(db, 'offers'),
      where('coffee_shop_id', '==', coffeeShopId),
      where('offer_type', '==', 'retail'),
      orderBy('createdAt', 'desc')
    ));
    const data = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    setOffers(data);
  };

  const handleUploadImage = async (offerId) => {
    if (!imageFile) return '';
    const imageRef = ref(storage, `retail_offers/${offerId}.jpg`);
    await uploadBytes(imageRef, imageFile);
    return await getDownloadURL(imageRef);
  };

  const handleSubmit = async () => {
    if (!title || !description || !price || !discountValue) {
      setMessage(t('retailOfferPage.error_missing_fields'));
      return;
    }

    if (editMode && editOfferId) {
      const updateData = {
        title,
        description,
        price: Number(price),
        discount_type: discountType,
        discount_value: Number(discountValue),
        min_requirement: minRequirement ? Number(minRequirement) : 0,
        active
      };
      if (imageFile) {
        const imageUrl = await handleUploadImage(editOfferId);
        updateData.image_url = imageUrl;
      }
      await updateDoc(doc(db, 'offers', editOfferId), updateData);
      setMessage(t('retailOfferPage.success_updated'));
    } else {
      const docRef = await addDoc(collection(db, 'offers'), {
        title,
        description,
        price: Number(price),
        discount_type: discountType,
        discount_value: Number(discountValue),
        min_requirement: minRequirement ? Number(minRequirement) : 0,
        coffee_shop_id: coffeeShopId,
        coffee_shop_name: coffeeShopName,
        offer_type: 'retail',
        active,
        createdAt: serverTimestamp()
      });
      const imageUrl = await handleUploadImage(docRef.id);
      if (imageUrl) await updateDoc(docRef, { image_url: imageUrl });
      setMessage(t('retailOfferPage.success_added'));
    }

    fetchOffers();
    clearForm();
  };

  const clearForm = () => {
    setTitle('');
    setDescription('');
    setPrice('');
    setDiscountValue('');
    setMinRequirement('');
    setImageFile(null);
    setActive(true);
    setEditMode(false);
    setEditOfferId(null);
  };

  const handleEdit = (offer) => {
    setEditMode(true);
    setEditOfferId(offer.id);
    setTitle(offer.title);
    setDescription(offer.description);
    setPrice(offer.price);
    setDiscountType(offer.discount_type);
    setDiscountValue(offer.discount_value);
    setMinRequirement(offer.min_requirement || '');
    setActive(offer.active);
  };

  const handleDelete = async (id) => {
    await deleteDoc(doc(db, 'offers', id));
    fetchOffers();
    setMessage(t('retailOfferPage.success_deleted'));
  };

  return (
    <div className="manage-offers-container">
      <h2>{t('retailOfferPage.title')}</h2>

      <div className="offer-form">
        <input placeholder={t('retailOfferPage.offer_title_placeholder')} value={title} onChange={e => setTitle(e.target.value)} />
        <input placeholder={t('retailOfferPage.offer_description_placeholder')} value={description} onChange={e => setDescription(e.target.value)} />
        <input type="number" placeholder={t('retailOfferPage.product_price')} value={price} onChange={e => setPrice(e.target.value)} />
        <select value={discountType} onChange={e => setDiscountType(e.target.value)}>
          <option value="percentage">{t('retailOfferPage.percentage_discount')}</option>
          <option value="fixed">{t('retailOfferPage.fixed_discount')}</option>
        </select>
        <input type="number" placeholder={t('retailOfferPage.discount_value')} value={discountValue} onChange={e => setDiscountValue(e.target.value)} />
        <input type="number" placeholder={t('retailOfferPage.min_requirement_optional')} value={minRequirement} onChange={e => setMinRequirement(e.target.value)} />
        <input type="file" onChange={(e) => setImageFile(e.target.files[0])} />
        <label>
          {t('retailOfferPage.active_label')}
          <input type="checkbox" checked={active} onChange={() => setActive(prev => !prev)} />
        </label>
        <button onClick={handleSubmit}>{editMode ? t('retailOfferPage.update_offer') : t('retailOfferPage.add_offer')}</button>
        {editMode && <button onClick={clearForm}>{t('retailOfferPage.cancel')}</button>}
      </div>

      {message && <p className="notification">{message}</p>}

      <div className="existing-offers">
        <h3>{t('retailOfferPage.existing_offers')}</h3>
        {offers.map(offer => (
          <div key={offer.id} className="offer-card">
            {offer.image_url && <img src={offer.image_url} alt="Offer" className="offer-img" />}
            <h4>{offer.title}</h4>
            <p>{offer.description}</p>
            <p>{t('retailOfferPage.price')}: {offer.price} €</p>
            <p>{t('retailOfferPage.discount')}: {offer.discount_value} {offer.discount_type === 'percentage' ? '%' : '€'}</p>
            <p>{t('retailOfferPage.status')}: {offer.active ? t('retailOfferPage.active') : t('retailOfferPage.inactive')} ✅</p>
            <button onClick={() => handleEdit(offer)}>{t('retailOfferPage.edit')}</button>
            <button onClick={() => handleDelete(offer.id)}>{t('retailOfferPage.delete')}</button>
          </div>
        ))}
      </div>

      <button onClick={() => navigate('/dashboard')}>{t('retailOfferPage.dashboard_back')}</button>
    </div>
  );
};

export default ManageRetailOffers;
